import { POOLS_MAP } from "../constants/pools/PoolsMap"
import { PoolName } from "../constants/pools/types/PoolName"
import { PoolTypes } from "../constants/pools/types/PoolTypes"

import { useEffect, useState } from "react"

import { AppState } from "../state"
import { BigNumber } from "@ethersproject/bignumber"
import { Contract } from "ethcall"
import LPTOKEN_UNGUARDED_ABI from "../constants/abis/lpTokenUnguarded.json"
import { LpTokenUnguarded } from "../../types/ethers-contracts/LpTokenUnguarded"
import { MulticallContract } from "../types/ethcall"
import { getMulticallProvider } from "../utils"
import { parseUnits } from "@ethersproject/units"
import { useActiveWeb3React } from "."
import { useSelector } from "react-redux"
import { isNetworkSupported } from "../utils/isNetworkSupported"

export default function usePoolTVLs(): { [poolName in PoolName]?: BigNumber } {
  const { chainId, library } = useActiveWeb3React()
  const btcUSDPrice = useSelector((state: AppState) => state.application.tokenPricesUSD?.BTC)
  const ethUSDPrice = useSelector((state: AppState) => state.application.tokenPricesUSD?.ETH)
  const [poolTvls, setPoolTvls] = useState<
    { [poolName in PoolName]?: BigNumber }
  >({})
  const hasPoolTvls = Object.keys(poolTvls).length > 0
  useEffect(() => {
    if (
      hasPoolTvls && // only run once
      btcUSDPrice &&
      ethUSDPrice
    ) {
      return
    }
    async function fetchTVLs() {
      if (!library || !chainId || (chainId && !isNetworkSupported(chainId))) {
        return
      }
      const ethcallProvider = await getMulticallProvider(library, chainId)

      const pools = Object.values(POOLS_MAP).filter(
        ({ addresses }) => addresses[chainId],
      )
      const supplyCalls = pools
        .map((p) => {
          return new Contract(
            p.lpToken.addresses[chainId],
            LPTOKEN_UNGUARDED_ABI,
          ) as MulticallContract<LpTokenUnguarded>
        })
        .map((c) => c.totalSupply())
      const tvls = await ethcallProvider.all(supplyCalls, {})
      // TODO Investigate, why pools tvl calculation logic differs from usePoolData
      const tvlsUSD = pools.map((pool, i) => {
        const tvlAmount = tvls[i]
        let tokenValue = 0
        if (pool.type === PoolTypes.BTC) {
          tokenValue = btcUSDPrice ?? 0
        } else if (pool.type === PoolTypes.ETH) {
          tokenValue = ethUSDPrice ?? 0
        } else {
          tokenValue = 1 // USD
        }
        return parseUnits(tokenValue.toFixed(2), 2)
          .mul(tvlAmount)
          .div(BigNumber.from(10).pow(2)) //1e18
      })
      setPoolTvls((prevState) => {
        return pools.reduce(
          (acc, pool, i) => ({
            ...acc,
            [pool.name]: tvlsUSD[i],
          }),
          prevState,
        )
      })
    }
    void fetchTVLs().catch((error) => {
      console.error(error)
    })
  }, [chainId, library, btcUSDPrice, ethUSDPrice, hasPoolTvls])
  return poolTvls
}
